import {
  Layers3,
  ArrowUpCircle,
  Workflow,
  CloudCog,
  LifeBuoy,
  ShieldCheck,
  Code2,
  GitBranch,
  ClipboardCheck,
} from "lucide-react";

export const CONTACT = {
  email: "connect@sphereitsolutions.com",
  phone: "+352 621725201",
  calendlyUrl: "#", // Placeholder — replace with real Calendly link
  offices: [
    {
      city: "Luxembourg",
      lines: ["7B, Rue de Steinfort", "Hobscheid, L-8371", "Luxembourg"],
    },
  ],
};

export const SERVICES = [
  {
    slug: "architecture",
    icon: Layers3,
    title: "Architecture",
    short:
      "Target-state architecture and solution design that scales with your bank.",
    description:
      "We design resilient, future-ready core banking architectures — from solution blueprints and environment strategy to performance engineering and high-availability design.",
    outcomes: [
      "Reference architecture aligned to core banking best practice",
      "Scalable, resilient and audit-ready core",
      "Clear roadmap from current to target state",
    ],
  },
  {
    slug: "business-analysis",
    icon: ClipboardCheck,
    title: "Business Analysis",
    short:
      "Translate business objectives into clear requirements and practical solution proposals.",
    description:
      "We translate business objectives into clear IT requirements and practical solution proposals — from stakeholder engagement and process analysis to functional specifications and solution design.",
    outcomes: [
      "Clear business and IT requirements aligned with strategic objectives",
      "Practical solution proposals that bridge business and technology",
      "Improved stakeholder alignment and more efficient delivery",
    ],
  },
  {
    slug: "development",
    icon: Code2,
    title: "Development",
    short:
      "Local development to extend your core with new functionality and customisations.",
    description:
      "We build customised developments on top of your out-of-the-box core. New functionality, bespoke customisations, product and API extensions are delivered in an upgrade-safe way.",
    outcomes: [
      "New capabilities beyond out-of-the-box functionality",
      "Upgrade-safe, standards-aligned customisations",
      "Faster delivery of bank-specific requirements",
    ],
  },
  {
    slug: "migration",
    icon: ArrowUpCircle,
    title: "Migration / Upgrade",
    short: "De-risked release upgrades and data migrations, delivered on time.",
    description:
      "Move to the latest core banking releases and migrate data with confidence. Our proven upgrade factory minimises downtime, protects data integrity and keeps regulators comfortable.",
    outcomes: [
      "Predictable, low-risk upgrade path",
      "Automated data validation and reconciliation",
      "Minimal business disruption",
    ],
  },
  {
    slug: "integration",
    icon: Workflow,
    title: "Integration / API",
    short: "Connect core banking to your ecosystem with clean, governed APIs.",
    description:
      "We build robust integrations between core banking platforms and payments, channels, CRM and third-party fintech using event-driven design and modern API gateways.",
    outcomes: [
      "Real-time, event-driven integration",
      "Reusable, well-governed API layer",
      "Faster time-to-market for new products",
    ],
  },
  {
    slug: "cloud",
    icon: CloudCog,
    title: "Cloud Modernization",
    short: "Take core banking to the cloud — securely and cost-effectively.",
    description:
      "Modernize your core on AWS, Azure or GCP with containerisation, infrastructure-as-code and automated pipelines, while meeting the security and compliance demands of financial services.",
    outcomes: [
      "Elastic, cloud-native core banking",
      "Lower total cost of ownership",
      "Automated, compliant deployments",
    ],
  },
  {
    slug: "support",
    icon: LifeBuoy,
    title: "Managed Support",
    short: "24/7 application support and continuous improvement.",
    description:
      "Keep your core running with SLA-backed managed services — incident management, release management, monitoring and proactive optimisation handled by core banking specialists.",
    outcomes: [
      "SLA-backed L2 / L3 support",
      "Proactive monitoring and tuning",
      "Continuous, predictable improvement",
    ],
  },
  {
    slug: "qa",
    icon: ShieldCheck,
    title: "Testing / QA",
    short: "Automated, regulatory-grade quality assurance.",
    description:
      "Protect every release with a comprehensive QA practice — functional, regression, performance and security testing, backed by core banking test automation accelerators.",
    outcomes: [
      "Automated regression coverage",
      "Performance validated at scale",
      "Confident, defect-free go-lives",
    ],
  },
  {
    slug: "operational-optimisation",
    icon: GitBranch,
    title: "Operational Optimisation",
    short:
      "Improve delivery efficiency, automation and time to market.",
    description:
      "We optimise engineering and operational processes to improve delivery efficiency, automation, and time to market. From streamlined development workflows to automated CI/CD pipelines, we help establish repeatable and reliable delivery practices that enable teams to release changes faster and with greater confidence.",
    outcomes: [
      "Shorter release cycles",
      "Reduced manual effort",
      "More consistent deployments and improved time to market",
    ],
  },
];

const HOME_SERVICE_SLUGS = ["architecture", "business-analysis", "development", "cloud", "qa"];

export const HOME_SERVICES = HOME_SERVICE_SLUGS.map((slug) => {
  const service = SERVICES.find((item) => item.slug === slug);

  if (slug === "business-analysis") {
    return {
      ...service,
      homeShort: "Translate business needs into clear IT requirements",
      homePath: { pathname: "/services", hash: "#business-analysis" },
    };
  }

  return service;
});

export const HOME_SERVICES_GRID_CLASS = "grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6";

export const CAROUSEL_SERVICES = [
  ...HOME_SERVICES,
  ...SERVICES.filter((service) => !HOME_SERVICES.some((item) => item.slug === service.slug)),
];

export const HOME_SERVICE_CARD_CLASS = "group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-blue-200";

export const HOME_SERVICE_TITLE_CLASS = "mt-5 min-h-[3.5rem] flex items-start";

export const SERVICE_CARD_WRAPPER_CLASS = "relative -mt-[60svh] first:mt-0 last:mb-24 min-h-[calc(var(--service-card-height,0px)+75svh)] pb-14 md:mt-0 md:last:mb-0 md:sticky md:min-h-0 md:pb-6";

export const SERVICE_CARD_PANEL_CLASS = "service-card-panel raised-surface group overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 md:p-12 hover:border-[#C8A45D] transition-all duration-300";

export const TILE_GRADIENTS = [
  "from-blue-500 to-cyan-400",
  "from-indigo-500 to-blue-400",
  "from-sky-500 to-cyan-400",
  "from-cyan-500 to-teal-400",
  "from-violet-500 to-indigo-400",
  "from-blue-600 to-sky-400",
  "from-teal-500 to-cyan-400",
];

export const STATS = [
  { value: "15+", label: "Years in core banking" },
  { value: "40+", label: "Core banking programmes delivered" },
  { value: "12", label: "Countries served" },
  { value: "99.9%", label: "Support SLA attainment" },
];

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1653299832314-5d3dc1e5a83c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMDNkJTIwc3BoZXJlJTIwYmx1ZXxlbnwwfHx8fDE3ODE0NjQyNjd8MA&ixlib=rb-4.1.0&q=85",
  team: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHRlYW0lMjBjb3Jwb3JhdGUlMjBtZWV0aW5nfGVufDB8fHx8MTc4MTQ2NDI2N3ww&ixlib=rb-4.1.0&q=85",
  conversation:
    "https://images.unsplash.com/photo-1758518729759-f580dc06770f?auto=format&fit=crop&w=2400&q=85",
  bankingTechnology:
    "https://images.unsplash.com/photo-1506399558188-acca6f8cbf41?auto=format&fit=crop&w=2400&q=85",
  architecture:
    "https://images.unsplash.com/photo-1617761141732-d481912af1a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMGJsdWUlMjBtb2Rlcm58ZW58MHx8fHwxNzgxNDY0MjY3fDA&ixlib=rb-4.1.0&q=85",
  data: "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBuZXR3b3JrJTIwYmx1ZXxlbnwwfHx8fDE3ODE0NjQyODF8MA&ixlib=rb-4.1.0&q=85",
  difference:
    "https://images.unsplash.com/photo-1758873269035-aae0e1fd3422?auto=format&fit=crop&w=2400&q=85",
  security:
    "https://images.unsplash.com/photo-1728756666032-d0b5552b6384?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxzZWN1cml0eSUyMHNoaWVsZCUyMHRlY2hub2xvZ3klMjBibHVlfGVufDB8fHx8MTc4MTQ2NDI4MXww&ixlib=rb-4.1.0&q=85",
};
